import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../components/Layout/Header';
import ArticleCard from '../components/ArticleCard';
import Filters from '../components/Filters';
import ArticleDetailModal from '../components/ArticleDetailModal';
import LoadingSpinner from '../components/LoadingSpinner';
import SubscriptionStatus from '../components/Subscription/SubscriptionStatus';
import TrialReminderModal from '../components/Subscription/TrialReminderModal';
import SubscriptionInterestForm from '../components/Subscription/SubscriptionInterestForm';
import TrialExpiredModal from '../components/Subscription/TrialExpiredModal';
import NewsFlashGame, { GAME_NAMES } from '../components/NewsFlash/NewsFlashGame';
import { articleAPI, subscriptionAPI, Article } from '../services/api';
import { format, isSameDay} from 'date-fns';
import { BoltIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';

interface HomePageProps {
  ageGroup: '6-9' | '10-13' | '14+';
  studentName?: string;
  isAdmin?: boolean;
}

// A day carries 13 to 19 stories (22 days to 29 Sep 2026: min 13, max 19, mean
// 15.6), so at the old 12 the feed never once showed a complete day without the
// student scrolling. 30 clears the busiest day with room to spare and makes the
// usual day a single request. Pagination below still works if a day ever runs
// past it. The backend now sends one age version per article instead of three,
// so 30 articles costs less over the wire than 12 used to.
const PAGE_SIZE = 30;

// De-duplicate a list of articles by id (defensive against any repeated cards).
const dedupeById = (items: Article[]): Article[] => {
  const seen = new Set<string | number>();
  const out: Article[] = [];
  for (const a of items) {
    if (!seen.has(a.id)) {
      seen.add(a.id);
      out.push(a);
    }
  }
  return out;
};

// Append only articles whose id isn't already present — a card can never appear twice.
const mergeUniqueById = (prev: Article[], next: Article[]): Article[] => {
  const seen = new Set<string | number>(prev.map(a => a.id));
  const merged = [...prev];
  for (const a of next) {
    if (!seen.has(a.id)) {
      seen.add(a.id);
      merged.push(a);
    }
  }
  return merged;
};

const HomePage: React.FC<HomePageProps> = ({ ageGroup, studentName = 'Explorer', isAdmin = false }) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [refreshKey, setRefreshKey] = useState(0);
  // True once the student picks a date by hand; until then the feed stays
  // pinned to today so an app left open overnight rolls over on its own.
  const dateWasChosenRef = useRef(false);
  // Identifies the newest feed request. Any in-flight "load more" whose id no
  // longer matches is discarded, so a page-2 response for the old filter can
  // never be merged into a freshly filtered list.
  const feedRequestRef = useRef(0);
  // Mirrors `articles` so loadMore can merge without depending on it (which
  // would rebuild the callback on every page and retrigger infinite scroll).
  const articlesRef = useRef<Article[]>([]);
  // Which feed is on screen — age group, date and category. Lets a refresh tell
  // itself apart from a genuine change of feed, so it can refetch to the same
  // depth instead of truncating back to one page.
  const feedIdentityRef = useRef('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<Array<{ name: string; count: number }>>([]);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showNewsFlash, setShowNewsFlash] = useState(false);

  // Subscription modal states
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [showTrialReminder, setShowTrialReminder] = useState(false);
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  // Check subscription status and determine which modal to show (skip for admin users)
  useEffect(() => {
    // Skip subscription checks for admin users
    if (isAdmin) {
      return;
    }

    const checkSubscriptionStatus = async () => {
      try {
        const status = await subscriptionAPI.getStatus();
        setSubscriptionStatus(status);

        // Determine which modal to show based on trial status
        if (status.subscription_status === 'trial_active' && status.days_remaining !== null) {
          const daysRemaining = status.days_remaining;

          // Show trial reminders on days 10, 12, and 14 (when 4, 2, or 0 days remaining)
          const reminderDays = [4, 2, 0];

          if (reminderDays.includes(daysRemaining)) {
            // Check if we've already shown reminder today
            const lastReminderDate = localStorage.getItem('last_trial_reminder_date');
            const today = new Date().toDateString();

            if (lastReminderDate !== today) {
              setShowTrialReminder(true);
              localStorage.setItem('last_trial_reminder_date', today);
            }
          }
        } else if (status.subscription_status === 'trial_expired') {
          // Show expired modal if trial has expired and user hasn't already dismissed it today
          const lastExpiredDismiss = localStorage.getItem('last_expired_modal_dismiss');
          const today = new Date().toDateString();

          if (lastExpiredDismiss !== today) {
            setShowExpiredModal(true);
          }
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    checkSubscriptionStatus();
  }, [isAdmin]);

  // Fetch categories whenever age group or date changes (cancellation-guarded)
  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      try {
        const stats = await articleAPI.getCategories(ageGroup, dateStr);
        if (!cancelled) setCategories(stats.map(s => ({ name: s.category, count: s.count })));
      } catch (error) {
        console.error('Error fetching categories:', error);
        try {
          const stats = await articleAPI.getCategories(ageGroup);
          if (!cancelled) setCategories(stats.map(s => ({ name: s.category, count: s.count })));
        } catch (fallbackError) {
          console.error('Error fetching categories (fallback):', fallbackError);
          if (!cancelled) setCategories([]);
        }
      }
    };
    loadCategories();
    return () => { cancelled = true; };
    // refreshKey belongs here too: the category chips and the "N Articles
    // Today" count are derived from this call, so without it the articles
    // refreshed while the count and the category list stayed on the morning's
    // numbers until the student changed the date and changed it back.
  }, [ageGroup, selectedDate, refreshKey]);

  // Load the first page whenever age group, date, or category changes, and
  // whenever `refreshKey` is bumped by one of the refresh triggers below.
  // The cancellation guard ensures a slower, older response can never overwrite
  // the results of a newer filter selection (fixes "filter shows wrong articles").
  useEffect(() => {
    let cancelled = false;
    // Invalidate any "load more" already in flight: its page 2 belongs to the
    // previous filter and must not be merged into these results.
    feedRequestRef.current += 1;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    // A refresh must not shrink the feed. This effect refetches page one and
    // replaces the whole list, so a student who had scrolled in more stories
    // lost them every time the app came back to the foreground — the feed
    // dropped from seventeen articles to twelve for no visible reason. When the
    // feed is the same one as last time (same age group, date and category) and
    // only refreshKey moved, refetch as deep as it already was.
    const identity = `${ageGroup}|${dateStr}|${selectedCategory ?? ''}`;
    const sameFeed = feedIdentityRef.current === identity;
    feedIdentityRef.current = identity;
    const pagesLoaded = sameFeed
      ? Math.min(3, Math.max(1, Math.ceil(articlesRef.current.length / PAGE_SIZE)))
      : 1;
    const fetchSize = PAGE_SIZE * pagesLoaded;   // the API caps page_size at 100

    const loadFirstPage = async () => {
      setLoading(true);
      try {
        const { articles: newArticles, total } = await articleAPI.getArticles(
          ageGroup,
          dateStr,
          selectedCategory || undefined,
          1,
          fetchSize
        );
        if (cancelled) return;
        const deduped = dedupeById(newArticles);
        // Only swap the list in when it actually differs. The three-minute
        // refresh poll used to hand React a brand new array every time, which
        // re-rendered every card and reset each image back to its grey
        // placeholder until it reloaded — images appeared to vanish at random
        // while a student was reading.
        //
        // The comparison has to include the age group, not just the id. All
        // three age groups carry the same stories in the same order, so the
        // staff age-group toggle returns an identical list of ids with every
        // headline and summary rewritten. Comparing ids alone judged that
        // "unchanged" and kept the previous age group's words on screen, which
        // made the toggle look broken.
        setArticles((prev) => {
          const same =
            prev.length === deduped.length &&
            prev.every((a, i) => a.id === deduped[i].id
                              && a.age_group === deduped[i].age_group);
          return same ? prev : deduped;
        });
        // We may have fetched several pages' worth in one request, so record
        // how deep we are rather than always 1, or "load more" would re-request
        // page two and merge in articles already on screen.
        setPage(pagesLoaded);
        setHasMore(deduped.length < total);
      } catch (error) {
        if (!cancelled) console.error('Error fetching articles:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadFirstPage();
    return () => { cancelled = true; };
  }, [ageGroup, selectedDate, selectedCategory, refreshKey]);

  // The feed used to load exactly once per session. A student who opened the
  // app at 6am (before the day's articles were published) and left it open saw
  // an empty feed for the rest of the morning, and had to force-quit and
  // reopen. Three triggers keep it current:
  //
  //   1. returning to the foreground (the usual mobile refresh moment),
  //   2. a slow poll while the feed is empty, so an app left open at 6am
  //      picks up the day's stories on its own,
  //   3. the date rolling over past midnight while the app sits open.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // If the user never picked a date by hand, keep the feed pinned to today
      // rather than showing yesterday to someone who left the app open.
      if (!dateWasChosenRef.current && !isSameDay(selectedDate, new Date())) {
        setSelectedDate(new Date());
        return; // the date change reloads on its own
      }
      setRefreshKey((k) => k + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [selectedDate]);

  // Keep checking while today's stories are still being published. The feed
  // used to stop polling the moment the first article arrived, so the rest of
  // the morning's stories — and the category chips and the "N Articles Today"
  // count — only appeared if the student happened to background and reopen the
  // app.
  //
  // A refresh resets the list to page one, so it must never happen under a
  // student who is reading: it only runs when they are near the top of the
  // feed, and only while viewing today.
  useEffect(() => {
    if (loading) return;
    if (dateWasChosenRef.current && !isSameDay(selectedDate, new Date())) return;
    const everyMs = articles.length === 0 ? 60000 : 180000;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const nearTop = window.scrollY < 200;
      if (articles.length === 0 || nearTop) setRefreshKey((k) => k + 1);
    }, everyMs);
    return () => clearInterval(id);
  }, [articles.length, loading, selectedDate]);

  // Load the next page. Merges by id so a card can never be duplicated, even if
  // the backend ever returns an overlapping row across page boundaries.
  //
  // The two effects above each guard against a slow response landing after a
  // newer one; this needs the same protection and used to have none. A student
  // who scrolled — firing page 2 for the unfiltered feed — and then tapped a
  // category chip got the old page 2 merged onto the freshly filtered list,
  // putting a dozen articles from every category under the chip. The `loading`
  // flag does not cover this: it stops a new request starting, not one already
  // in flight from finishing.
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    const req = ++feedRequestRef.current;
    setLoading(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    try {
      const { articles: newArticles, total } = await articleAPI.getArticles(
        ageGroup,
        dateStr,
        selectedCategory || undefined,
        nextPage,
        PAGE_SIZE
      );
      // A newer filter, date or age group has been chosen since: drop this.
      if (req !== feedRequestRef.current) return;
      // Merged from the ref rather than inside the updater: the updater runs
      // later, so `total` could not be compared against the result there
      // without calling setHasMore from inside it.
      const merged = mergeUniqueById(articlesRef.current, newArticles);
      setArticles(merged);
      setHasMore(merged.length < total);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more articles:', error);
    } finally {
      if (req === feedRequestRef.current) setLoading(false);
    }
  }, [loading, hasMore, page, ageGroup, selectedDate, selectedCategory]);

  // Keep the mirror in step with the list.
  useEffect(() => {
    articlesRef.current = articles;
  }, [articles]);

  // Infinite scroll: load more when the sentinel enters view
  useEffect(() => {
    if (inView) loadMore();
  }, [inView, loadMore]);

  // Refetch selected article when age group changes (for modal content update)
  useEffect(() => {
    const refetchSelectedArticle = async () => {
      if (selectedArticle) {
        try {
          const articleId = typeof selectedArticle.id === 'string' ? parseInt(selectedArticle.id) : selectedArticle.id;
          const updatedArticle = await articleAPI.getArticle(articleId, ageGroup);

          // Merge with existing article to preserve fields not in ArticleVersionResponse (like category, keywords)
          setSelectedArticle({
            ...selectedArticle,
            ...updatedArticle,
            id: selectedArticle.id, // Keep original id format
            category: selectedArticle.category, // Preserve category
            keywords: selectedArticle.keywords, // Preserve keywords
            age_group: ageGroup // Update age group
          });
        } catch (error) {
          console.error('Error refetching article for new age group:', error);
        }
      }
    };

    refetchSelectedArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ageGroup]);

  const getBackgroundStyle = () => {
    if (ageGroup === '6-9') {
      return 'bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50';
    } else if (ageGroup === '10-13') {
      return 'bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50';
    }
    return 'bg-gray-50';
  };

  // What the grid actually renders.
  //
  // Tapping a category used to leave the previous category's cards on screen
  // for the whole round trip — four to five seconds — because the grid renders
  // `articles` and the spinner only appears when that list is empty. The feed
  // already in hand contains this category's stories, so filtering it in the
  // browser puts the right cards up immediately; the request that follows
  // replaces them with the authoritative, complete set.
  //
  // Once that response lands `articles` is already filtered, so this filter is
  // a no-op on it and the displayed list is unchanged. Safe because the feed
  // and the category chips take their category values from the same enum.
  const visibleArticles = selectedCategory
    ? articles.filter(a => a.category === selectedCategory)
    : articles;

  const getEmptyStateMessage = () => {
    if (ageGroup === '6-9') {
      return {
        icon: '🎈',
        title: 'Nothing New Yet Today!',
        message: 'Check back soon for exciting stories!'
      };
    } else if (ageGroup === '10-13') {
      return {
        icon: '📚',
        title: 'No Articles Available',
        message: 'New content is added daily. Try checking another date!'
      };
    }
    return {
      icon: '📰',
      title: 'No Articles Found',
      message: 'Please try a different date or category.'
    };
  };

  return (
    <div className={`min-h-screen ${getBackgroundStyle()}`}>
      <Header ageGroup={ageGroup} studentName={studentName} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subscription Status - Hidden for admin users */}
        {!isAdmin && (
          <div className="mb-6">
            <SubscriptionStatus />
          </div>
        )}

        {/* NewsFlash launch tile */}
        <motion.button
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setShowNewsFlash(true)}
          className={`w-full mb-6 rounded-2xl shadow-md hover:shadow-xl transition-all active:scale-[0.99] text-white text-left overflow-hidden
            ${ageGroup === '6-9'
              ? 'bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400'
              : ageGroup === '10-13'
              ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-indigo-500'
              : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-700'}`}
        >
          <div className="flex items-center justify-between px-5 py-4 sm:px-6 sm:py-5">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-2xl bg-white/20 grid place-items-center">
                <BoltIcon className="h-7 w-7" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg sm:text-xl font-extrabold tracking-tight">Play {GAME_NAMES[ageGroup]}</h3>
                  <span className="text-[10px] font-bold bg-white/25 px-2 py-0.5 rounded-full uppercase tracking-wide">⏱ 99 sec</span>
                </div>
                <p className="text-sm text-white/90">Today's quiz — race the clock for a high score!</p>
              </div>
            </div>
            <ChevronRightIcon className="h-6 w-6 flex-shrink-0" />
          </div>
        </motion.button>

        {/* Filters */}
        <Filters
          selectedDate={selectedDate}
          selectedCategory={selectedCategory}
          categories={categories}
          onDateChange={(d: Date) => { dateWasChosenRef.current = true; setSelectedDate(d); }}
          onCategoryChange={setSelectedCategory}
          ageGroup={ageGroup}
        />

        {/* Articles Grid */}
        {/* Keyed on the visible list, not the raw one: picking a category with
            nothing already loaded should spin, not claim there are no stories. */}
        {loading && visibleArticles.length === 0 ? (
          <LoadingSpinner ageGroup={ageGroup} />
        ) : visibleArticles.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="text-6xl mb-4">{getEmptyStateMessage().icon}</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              {getEmptyStateMessage().title}
            </h3>
            <p className="text-gray-600">
              {getEmptyStateMessage().message}
            </p>
          </motion.div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-max">
              <AnimatePresence mode="sync">
                {visibleArticles.map((article, index) => (
                  <motion.div
                    key={article.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, height: 0, marginBottom: 0 }}
                    transition={{
                      layout: { duration: 0.3 },
                      opacity: { duration: 0.2 },
                      delay: index < 12 ? index * 0.05 : 0
                    }}
                  >
                    <ArticleCard
                      article={article}
                      ageGroup={ageGroup}
                      priority={index < 4}
                      onClick={() => setSelectedArticle(article)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Load More */}
            {hasMore && (
              <div ref={loadMoreRef} className="py-8 text-center">
                {loading ? (
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <button
                    onClick={loadMore}
                    className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Load More Articles
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Sourcing line. Sits outside the articles branch so it shows on an
            empty feed too. */}
        <p className="mt-10 mb-2 text-center text-xs text-gray-400">
          With inputs from IANS and the Knoozy Bureau
        </p>
      </main>

      {/* Article Detail Modal */}
      <AnimatePresence>
        {selectedArticle && (
          <ArticleDetailModal
            article={selectedArticle}
            ageGroup={ageGroup}
            onClose={() => setSelectedArticle(null)}
          />
        )}
      </AnimatePresence>

      {/* NewsFlash Game */}
      <AnimatePresence>
        {showNewsFlash && (
          <NewsFlashGame ageGroup={ageGroup} onClose={() => setShowNewsFlash(false)} />
        )}
      </AnimatePresence>

      {/* Trial Reminder Modal (Days 10, 12, 14) */}
      <TrialReminderModal
        isOpen={showTrialReminder}
        onClose={() => setShowTrialReminder(false)}
        daysRemaining={subscriptionStatus?.days_remaining || 0}
        totalArticlesRead={subscriptionStatus?.total_articles_read || 0}
        onExpressInterest={() => {
          setShowTrialReminder(false);
          setShowInterestForm(true);
        }}
      />

      {/* Subscription Interest Form */}
      <SubscriptionInterestForm
        isOpen={showInterestForm}
        onClose={() => setShowInterestForm(false)}
        onSuccess={() => {
          setShowInterestForm(false);
          // Optionally refresh subscription status
        }}
      />

      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showExpiredModal}
        onSubscribe={() => {
          setShowExpiredModal(false);
          setShowInterestForm(true);
        }}
        onDecline={() => {
          setShowExpiredModal(false);
          // Store that user dismissed the modal today
          localStorage.setItem('last_expired_modal_dismiss', new Date().toDateString());
        }}
      />
    </div>
  );
};

export default HomePage;